//@flow

import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Modal,
  Button,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Label,
  FormGroup,
} from 'reactstrap';

import makeUrl from '../../../lib/makeUrl';

import Toggler from '../../UI/Toggler';
import DatePicker from '../../UI/DatePicker';

import { openLoginModal } from '../../../redux/actions/authActions';
import {
  openOverviewModal,
  closeOverviewModal,
} from '../../../redux/actions/trekOverviewActions';
import { useTrekAsTemplate } from '../../../redux/actions/useAsTemplateActions';

import { ForkGuidedSchema, ForkSelfGuidedSchema } from '../constants';
import { Formik, Field } from 'formik';

type Props = {
  children?: Object,
  trekId: string,
  history: Object,
  isLoggedIn: boolean,
  isLeader: boolean,
  openOverviewModal: () => void,
  useTrekAsTemplate: () => void,
  closeOverviewModal: () => void,
};

type State = { guidedOrSelfguided: string };

const guidedComponents = [
  { title: 'Trek Price (USD)', key: 'price', placeholder: '1200' },
  { title: 'Min Group Size', key: 'min', placeholder: '1' },
  { title: 'Max Group Size', key: 'max', placeholder: '11' },
];

class UseAsTemplateButton extends Component<Props, State> {
  state = {
    guidedOrSelfguided: '',
  };

  render() {
    const { children } = this.props;
    const { guidedOrSelfguided } = this.state;

    const modalHeader = this.isGuided() ? 'GUIDED' : 'CONFIRM YOUR TREK START DATE';

    const label = this.isGuided() && <Label>Trek Start Date</Label>;

    return (
      <Toggler>
        {({ on, open, toggle }) => (
          <Fragment>
            {children ? this.renderChildren(open) : this.renderButton(open)}
            <Modal isOpen={on} className="before-buy">
              {guidedOrSelfguided ? (
                <Formik
                  initialValues={{
                    start_date: '',
                    price: '',
                    min: '',
                    max: '',
                  }}
                  validationSchema={this.renderSchema}
                  onSubmit={async (values, { setSubmitting }) => {
                    await this.useAsTemplate(values);
                    setSubmitting(false);
                  }}
                  render={({ handleSubmit, errors, isSubmitting }) => (
                    <form onSubmit={handleSubmit}>
                      <ModalHeader toggle={toggle}>{modalHeader}</ModalHeader>
                      <ModalBody>
                        <FormGroup>
                          {label}
                          <Field
                            render={({ form: { setFieldValue } }) => (
                              <DatePicker
                                onDateChange={date => setFieldValue('start_date', date)}
                                dateFormat="YYYY-MM-DD"
                                notInline={this.isGuided()}
                              />
                            )}
                          />
                          {errors.start_date && (
                            <p className="error">{errors.start_date}</p>
                          )}
                        </FormGroup>
                        {this.isGuided() &&
                          guidedComponents.map(v => (
                            <FormGroup key={v.key}>
                              <Label>{v.title}</Label>
                              <Field
                                name={v.key}
                                component="input"
                                placeholder={v.placeholder}
                                className="form-control"
                                type="number"
                              />
                              {errors[v.key] && (
                                <p className="error">{this.reformatError(errors[v.key])}</p>
                              )}
                            </FormGroup>
                          ))}
                      </ModalBody>
                      <ModalFooter>
                        <Button color="yellow" onClick={this.changeState}>
                          BACK
                        </Button>
                        <Button
                          color="yellow"
                          className={isSubmitting ? 'btn-loading' : ''}
                          type="submit"
                        >
                          CONTINUE
                        </Button>
                      </ModalFooter>
                    </form>
                  )}
                />
              ) : (
                <Fragment>
                  <ModalHeader toggle={toggle}>
                    Choose a Trek you want to create
                  </ModalHeader>
                  <div className="modal-body">
                    <div className="text-center">
                      <Button
                        color="yellow"
                        name="guided"
                        className="mr-3"
                        onClick={this.handleState}
                      >
                        Guided
                      </Button>
                      <Button
                        color="yellow"
                        name="self guided"
                        className=""
                        onClick={this.changeState}
                      >
                        Self Guided
                      </Button>
                    </div>
                  </div>
                </Fragment>
              )}
            </Modal>
          </Fragment>
        )}
      </Toggler>
    );
  }
  renderSchema = () => (this.isGuided() ? ForkGuidedSchema : ForkSelfGuidedSchema);

  renderButton = onClick => (
    <Button onClick={() => this.useThisTrek(onClick)} color="primary">
      <i className="icon icon-hg-doc-text" /> USE THIS TREK
    </Button>
  );

  renderChildren = onClick => (
    <span onClick={() => this.useThisTrek(onClick)}>{this.props.children}</span>
  );

  useThisTrek = onClick => {
    this.changeState({ target: { name: '' } });
    onClick();
  };

  reformatError = error =>
    error.includes('must be a `number` type') ? 'Should be a number' : error;

  handleState = e => {
    const { isLeader, openOverviewModal } = this.props;

    if (isLeader) {
      this.changeState(e);
    } else {
      openOverviewModal({
        component: 'NeedToBeAGuide',
      });
    }
  };

  changeState = e =>
    this.setState({
      guidedOrSelfguided: e.target.name,
      trekStartDate: null,
      trekPrice: '',
      trekSize: { min: '', max: '' },
    });

  isGuided = () => this.state.guidedOrSelfguided === 'guided';

  useAsTemplate = async values => {
    const {
      trekId,
      history,
      isLoggedIn,
      openOverviewModal,
      useTrekAsTemplate,
      closeOverviewModal,
    } = this.props;

    const { start_date, min, max, price } = values;
    let patchObj = {};
    if (this.isGuided()) {
      patchObj = {
        start_date,
        price: { amount: +price, currency: 'USD' },
        group_size: { minimum: +min, maximum: +max },
        is_guided: true,
      };
    } else {
      patchObj = { start_date };
    }

    if (isLoggedIn) {
      try {
        const { id, name, region } = await useTrekAsTemplate(trekId, patchObj);
        const trekUrl = makeUrl.trekOverview(name, id, region);
        history.replace(trekUrl);
        closeOverviewModal();
      } catch (e) {
        //error trying to fork trek
      }
    } else {
      openOverviewModal({
        component: 'LoginToUseTemplate',
      });
    }
  };

  componentWillUnmount() {
    if (this.modalOpenTimeout) return clearTimeout(this.modalOpenTimeout);
  }
}
const mapStateToProps = ({ selectedTrek, user }) => ({
  trekId: selectedTrek.trek.id,
  trekName: selectedTrek.trek.name,
  trekRegion: selectedTrek.trek.region,
  isLoggedIn: Boolean(user.userId),
  isLeader: user.isLeader,
  roles: user.role,
});

export default withRouter(
  connect(
    mapStateToProps,
    {
      openLoginModal,
      openOverviewModal,
      useTrekAsTemplate,
      closeOverviewModal,
    }
  )(UseAsTemplateButton)
);



// WEBPACK FOOTER //
// ./src/components/TrekDetail/DayOverview/UseAsTemplateButton.jsx